// frontend/packages/business-console/src/components/leadForm/LeadTrendsCard.jsx
//
// "Leads by week" card on the Lead forms page: weekly real leads (spam
// excluded) for the selected form, split by the form's "How did you hear
// about us?" answer when it has one, plus a CSV the owner can forward to
// their marketing partner. Backed by GET /v2/lead-form/trends.
//
// Per-tenant feature (`lead_trends`, enabled from UpRyzze Tools). When the
// feature is off the endpoint returns { enabled: false } and this card
// renders nothing, so the page is unchanged for everyone else.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

import { useTokens, Surface } from '../primitives';
import { getLeadTrends } from '../../services/leadFormService';

const WEEK_OPTIONS = [8, 12, 26, 52];
const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

// 'YYYY-MM-DD' -> 'Oct 5' (calendar date, no timezone shifting).
export function shortWeek(ymd) {
  const [, m, d] = String(ymd).split('-').map(Number);
  return m && d ? `${MONTHS[m - 1]} ${d}` : String(ymd);
}

// Spreadsheet-safe cell: a leading = + - @ tab or CR would make Excel or
// Sheets run the cell as a formula (CSV injection), so prefix it with a
// quote. Column headers are option labels the tenant wrote, but the file is
// forwarded to third parties, so every cell is neutralised.
export function csvCell(value) {
  let s = String(value ?? '');
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

// Week of, Total, then one column per answer (busiest first).
export function buildTrendsCsv(trends) {
  const sources = trends?.sources || [];
  const header = ['Week of', 'Total', ...sources];
  const rows = (trends?.weeks || []).map((w) => [
    w.weekStart,
    w.total,
    ...sources.map((s) => w.bySource?.[s] || 0),
  ]);
  return [header, ...rows].map((r) => r.map(csvCell).join(',')).join('\n');
}

function downloadCsv(trends, weeks) {
  const csv = buildTrendsCsv(trends);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const slug = trends?.form?.slug || 'leads';
  const last = trends?.weeks?.[trends.weeks.length - 1]?.weekStart || 'now';
  a.href = url;
  a.download = `lead-trends-${slug}-${weeks}wk-${last}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function WeeklyBars({ t, weeks, highlight }) {
  const W = 600;
  const H = 140;
  const gap = weeks.length > 26 ? 2 : 6;
  const barW = Math.max(2, (W - gap * (weeks.length - 1)) / weeks.length);
  const max = Math.max(1, ...weeks.map((w) => w.total));
  const labelEvery = Math.ceil(weeks.length / 6);
  return (
    <Box sx={{ mt: 1.5 }}>
      <svg
        width="100%"
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        role="img"
        aria-label="Leads per week"
        style={{ display: 'block' }}
      >
        {weeks.map((w, i) => {
          const x = i * (barW + gap);
          const h = (w.total / max) * (H - 4);
          const hh = highlight
            ? ((w.bySource?.[highlight] || 0) / max) * (H - 4)
            : 0;
          return (
            <g key={w.weekStart}>
              <title>
                {`Week of ${shortWeek(w.weekStart)}: ${w.total} lead${
                  w.total === 1 ? '' : 's'
                }${
                  highlight
                    ? `, ${w.bySource?.[highlight] || 0} from ${highlight}`
                    : ''
                }`}
              </title>
              <rect
                x={x}
                y={H - h}
                width={barW}
                height={h}
                rx="2"
                fill={highlight ? t.blueBorder : t.blue}
              />
              {hh > 0 ? (
                <rect
                  x={x}
                  y={H - hh}
                  width={barW}
                  height={hh}
                  rx="2"
                  fill={t.blue}
                />
              ) : null}
            </g>
          );
        })}
      </svg>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mt: 0.5,
          color: t.faint,
          fontSize: 11,
        }}
      >
        {weeks
          .filter((_, i) => i % labelEvery === 0)
          .map((w) => (
            <span key={w.weekStart}>{shortWeek(w.weekStart)}</span>
          ))}
      </Box>
    </Box>
  );
}

export default function LeadTrendsCard({ formSlug }) {
  const t = useTokens();
  const [weeks, setWeeks] = useState(12);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getLeadTrends({ weeks, form: formSlug })
      .then((data) => {
        if (cancelled) return;
        setTrends(data);
        setError(false);
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        // A 4xx means "not for you": stay hidden. A 5xx or network failure
        // is a real problem: say so instead of silently vanishing (P4).
        if (status && status < 500) setTrends({ enabled: false });
        else setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [weeks, formSlug]);

  const total = useMemo(
    () => (trends?.weeks || []).reduce((s, w) => s + (w.total || 0), 0),
    [trends]
  );
  const topSource = trends?.sources?.[0] || '';
  const topCount = useMemo(
    () =>
      topSource
        ? (trends?.weeks || []).reduce(
            (s, w) => s + (w.bySource?.[topSource] || 0),
            0
          )
        : 0,
    [trends, topSource]
  );

  // Render nothing until the server says the feature is on, so tenants
  // without it never see the card (not even a loading flash).
  if (!trends?.enabled && !error) return null;
  if (error && !trends?.enabled) {
    return (
      <Surface sx={{ p: 2.25, mt: 2 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: t.text }}>
          Leads by week
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: t.muted, mt: 0.5 }}>
          We couldn&rsquo;t load your lead trends. Refresh the page to try
          again.
        </Typography>
      </Surface>
    );
  }

  const rows = trends?.weeks ? [...trends.weeks].reverse() : [];
  const sources = trends?.sources || [];

  return (
    <Surface sx={{ p: 2.25, mt: 2 }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 800, color: t.text }}>
            Leads by week
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: t.muted }}>
            Real form submissions, spam removed. Weeks start Monday.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel id="lt-weeks">Range</InputLabel>
            <Select
              labelId="lt-weeks"
              label="Range"
              value={weeks}
              onChange={(e) => setWeeks(Number(e.target.value))}
            >
              {WEEK_OPTIONS.map((n) => (
                <MenuItem key={n} value={n}>
                  Last {n} weeks
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            size="small"
            startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
            disabled={loading || !trends?.weeks?.length}
            onClick={() => downloadCsv(trends, weeks)}
            sx={{
              fontWeight: 700,
              fontSize: 12.5,
              textTransform: 'none',
              borderRadius: '9px',
              border: `1px solid ${t.cardBorder}`,
              color: t.text,
              px: '12px',
            }}
          >
            Download CSV
          </Button>
        </Box>
      </Box>

      {error && !loading ? (
        <Typography sx={{ fontSize: 12.5, color: t.red, mt: 1.5 }}>
          Couldn&rsquo;t refresh these numbers. Showing the last ones loaded.
        </Typography>
      ) : null}
      {loading ? (
        <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={22} />
        </Box>
      ) : (
        <>
          <Typography sx={{ fontSize: 13, color: t.textSecondary, mt: 1.5 }}>
            <b>{total}</b> lead{total === 1 ? '' : 's'} in the last {weeks}{' '}
            weeks
            {topSource && topCount > 0 ? (
              <>
                {', '}
                <b>{topCount}</b> said &ldquo;{topSource}&rdquo;
              </>
            ) : null}
            .
          </Typography>
          <WeeklyBars t={t} weeks={trends.weeks} highlight={topSource} />
          {topSource ? (
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                mt: 1,
                fontSize: 11.5,
                color: t.muted,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '2px',
                    bgcolor: t.blue,
                  }}
                />
                {topSource}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '2px',
                    bgcolor: t.blueBorder,
                  }}
                />
                All other leads
              </Box>
            </Box>
          ) : null}

          <Box sx={{ mt: 2, overflowX: 'auto' }}>
            <Box
              component="table"
              sx={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: 12.5,
                '& th, & td': {
                  textAlign: 'right',
                  py: 0.75,
                  px: 1,
                  borderTop: `1px solid ${t.hairline}`,
                  whiteSpace: 'nowrap',
                },
                '& th:first-of-type, & td:first-of-type': { textAlign: 'left' },
                '& th': { color: t.muted, fontWeight: 700, fontSize: 11.5 },
                '& td': { color: t.text },
              }}
            >
              <thead>
                <tr>
                  <th>Week of</th>
                  <th>Total</th>
                  {sources.map((s) => (
                    <th key={s}>{s}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((w) => (
                  <tr key={w.weekStart}>
                    <td>{shortWeek(w.weekStart)}</td>
                    <td>
                      <b>{w.total}</b>
                    </td>
                    {sources.map((s) => (
                      <td key={s}>{w.bySource?.[s] || 0}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Box>
          </Box>
          {!trends.sourceField ? (
            <Typography sx={{ fontSize: 11, color: t.faint, mt: 1.5 }}>
              Add a &ldquo;How did you hear about us?&rdquo; question to this
              form to see where leads come from. Your UpRyzze setup team can add
              it.
            </Typography>
          ) : null}
        </>
      )}
    </Surface>
  );
}
